import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { publicAPI } from '../../lib/api';
import { ArrowRight, MapPin, Wrench, ChevronRight, ChevronDown, ChevronUp, Upload, MessageSquare, Factory, FolderOpen, Layers } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';

export default function HomePage() {
  const [services, setServices] = useState([]);
  const [cities, setCities] = useState([]);
  const [content, setContent] = useState({});
  const [openFaq, setOpenFaq] = useState(null);
  const [faqItems, setFaqItems] = useState([]);
  const [showAllFaq, setShowAllFaq] = useState(false);

  useEffect(() => {
    publicAPI.getServices().then(r => setServices(r.data));
    publicAPI.getCities().then(r => setCities(r.data.slice(0, 12)));
    publicAPI.getContent('home').then(r => {
      setContent(r.data);
      // Parse FAQ items from CMS
      try {
        const faqs = JSON.parse(r.data.faq_items || '[]');
        setFaqItems(faqs);
      } catch {
        setFaqItems([]);
      }
    });
  }, []);

  const stats = [
    { value: content.stat_1_value || '500+', label: content.stat_1_label || 'Uretici', icon: Factory },
    { value: content.stat_2_value || '1200+', label: content.stat_2_label || 'Proje', icon: FolderOpen },
    { value: content.stat_3_value || '30+', label: content.stat_3_label || 'Sektor', icon: Layers },
  ];

  const steps = [
    { step: '1', icon: Upload, title: content.how_step1_title || 'Projeni Gonder', desc: content.how_step1_desc || 'Teknik ciziminizi veya uretim talebinizi yukleyin.' },
    { step: '2', icon: MessageSquare, title: content.how_step2_title || 'Teklifleri Al', desc: content.how_step2_desc || 'Dogrulanmis ureticiler projenizi incelesin ve size rekabetci teklifler sunsun.' },
    { step: '3', icon: Factory, title: content.how_step3_title || 'Uretime Basla', desc: content.how_step3_desc || 'En uygun ureticiyi secin ve uretim surecini baslatin.' },
  ];

  return (
    <>
      <Helmet>
        <title>Uretim Noktasi | Turkiye'nin Endustriyel Uretim Platformu</title>
        <meta name="description" content="CNC torna, lazer kesim, kalip uretimi ve endustriyel uretim hizmetleri icin Turkiye genelinde hizli teklif alin. UretimNoktasi.com ile uretim projelerinize dogru firmayi bulun." />
        <link rel="canonical" href="https://uretimnoktasi.com" />
        <meta property="og:title" content="Uretim Noktasi - Endustriyel Uretim Platformu" />
        <meta property="og:description" content="Turkiye genelinde CNC torna, lazer kesim ve uretim hizmetleri icin teklif alin." />
        <meta property="og:url" content="https://uretimnoktasi.com" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Uretim Noktasi - Endustriyel Uretim Platformu" />
        <meta name="twitter:description" content="CNC torna, lazer kesim, kalip uretimi ve endustriyel uretim hizmetleri icin Turkiye genelinde hizli teklif alin." />
        {faqItems.length > 0 && (
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqItems.map(f => ({
              "@type": "Question",
              "name": f.question,
              "acceptedAnswer": { "@type": "Answer", "text": f.answer }
            }))
          })}</script>
        )}
      </Helmet>

      {/* Hero */}
      <section className="relative bg-[hsl(221,83%,22%)] text-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(80%_60%_at_20%_0%,rgba(30,58,138,0.3)_0%,transparent_60%),radial-gradient(70%_50%_at_80%_10%,rgba(14,116,144,0.15)_0%,transparent_55%)]" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-blue-300 text-sm font-medium tracking-wider uppercase mb-4">
                {content.hero_label || "Turkiye'nin Endustriyel Uretim Platformu"}
              </p>
              <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-tight" data-testid="hero-title">
                {content.hero_title || 'CNC isleme, lazer kesim ve kalip uretimi icin hizli teklif alin'}
              </h1>
              <p className="mt-6 text-lg text-blue-100 leading-relaxed max-w-xl">
                {content.hero_subtitle || 'Turkiye genelindeki guvenilir ureticilerden projeleriniz icin rekabetci teklifler toplayin.'}
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/teklif-al">
                  <Button size="lg" className="bg-white text-[hsl(221,83%,22%)] hover:bg-blue-50 font-semibold px-8 h-12 text-base" data-testid="hero-cta-teklif-al">
                    {content.hero_cta || 'Teklif Al'}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a href="#nasil-calisir">
                  <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 h-12 text-base">
                    Nasil Calisir?
                  </Button>
                </a>
              </div>
            </div>
            <div className="hidden lg:block">
              <img
                src={content.hero_image || 'https://images.unsplash.com/photo-1764114235896-034c8772de01?w=600&h=450&fit=crop&auto=format&q=80'}
                alt="CNC isleme ve lazer kesim - endustriyel uretim"
                className="rounded-2xl shadow-2xl w-full h-[420px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-white border-b" data-testid="stats-bar">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 divide-x">
            {stats.map((stat, i) => (
              <div key={i} className="flex items-center justify-center gap-3 py-5" data-testid={`stat-${i}`}>
                <stat.icon className="h-6 w-6 text-[hsl(221,83%,22%)] hidden sm:block" />
                <div className="text-center sm:text-left">
                  <p className="text-2xl sm:text-3xl font-semibold text-[hsl(221,83%,22%)] tabular-nums">{stat.value}</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-20 bg-slate-50" id="nasil-calisir">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-2xl sm:text-3xl font-semibold">Nasil Calisir?</h2>
            <p className="mt-3 text-muted-foreground max-w-xl mx-auto">Uc basit adimda uretim projeniz icin en uygun firmayi bulun.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {steps.map((item, i) => (
              <div key={i} className="relative" data-testid={`how-step-${i}`}>
                <div className="bg-white rounded-2xl p-8 h-full border hover:shadow-lg transition-shadow">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-[hsl(221,83%,22%)] text-white flex items-center justify-center text-lg font-semibold flex-shrink-0">
                      {item.step}
                    </div>
                    <item.icon className="h-6 w-6 text-[hsl(221,83%,22%)]" />
                  </div>
                  <h3 className="font-semibold text-xl mb-3">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
                {i < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-6 lg:-right-8 -translate-y-1/2">
                    <ArrowRight className="h-5 w-5 text-slate-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link to="/teklif-al">
              <Button size="lg" className="font-semibold px-10 h-12 text-base">
                Hemen Teklif Al <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-semibold">Hizmetlerimiz</h2>
              <p className="mt-1 text-sm text-muted-foreground">Endustriyel uretim alaninda sunulan hizmetler</p>
            </div>
            <Link to="/hizmetler" className="text-sm font-medium text-[hsl(221,83%,22%)] flex items-center gap-1 hover:underline">
              Tumu <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map(s => (
              <Link key={s.id} to="/teklif-al">
                <Card className="bg-white hover:shadow-md transition-shadow h-full group" data-testid={`service-card-${s.slug}`}>
                  <CardContent className="p-6">
                    <Wrench className="h-8 w-8 text-[hsl(221,83%,22%)] mb-4" />
                    <h3 className="font-semibold text-lg mb-2 group-hover:text-[hsl(221,83%,22%)] transition-colors">{s.name}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{s.summary}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Cities */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-semibold">Hizmet Verdigimiz Sehirler</h2>
              <p className="mt-1 text-sm text-muted-foreground">Turkiye genelinde 81 ilde endustriyel uretim hizmetleri</p>
            </div>
            <Link to="/sehirler" className="text-sm font-medium text-[hsl(221,83%,22%)] flex items-center gap-1 hover:underline">
              81 Il <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {cities.map(c => (
              <Link key={c.id} to={`/${c.slug}`} className="flex items-center gap-2 p-3 rounded-lg border bg-white hover:border-slate-300 hover:shadow-sm transition-all" data-testid={`city-link-${c.slug}`}>
                <MapPin className="h-4 w-4 text-[hsl(221,83%,22%)] flex-shrink-0" />
                <span className="text-sm font-medium">{c.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faqItems.length > 0 && (
        <section className="py-16 bg-white" data-testid="home-faq-section">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-semibold text-center mb-10">Sikca Sorulan Sorular</h2>
            <div className="space-y-3">
              {(showAllFaq ? faqItems : faqItems.slice(0, 4)).map((faq, i) => (
                <div key={i} className="border rounded-xl bg-slate-50" data-testid={`home-faq-${i}`}>
                  <button
                    className="w-full flex items-center justify-between p-5 text-left"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span className="font-medium text-sm sm:text-base pr-4">{faq.question}</span>
                    {openFaq === i
                      ? <ChevronUp className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                      : <ChevronDown className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    }
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed border-t pt-4">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {!showAllFaq && faqItems.length > 4 && (
              <div className="mt-6 text-center">
                <Button variant="outline" onClick={() => setShowAllFaq(true)} data-testid="faq-show-more">
                  Daha Fazlasini Goster ({faqItems.length - 4} soru daha)
                </Button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-[hsl(221,83%,22%)] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-semibold mb-4">Uretim Projeniz Icin Teklif Alin</h2>
          <p className="text-blue-100 mb-8 max-w-2xl mx-auto text-lg leading-relaxed">
            Teknik cizimlerinizi yukleyin, proje detaylarinizi belirtin. Turkiye genelindeki uzman firmalardan rekabetci teklifler alin.
          </p>
          <Link to="/teklif-al">
            <Button size="lg" className="bg-white text-[hsl(221,83%,22%)] hover:bg-blue-50 font-semibold px-10 h-12 text-base" data-testid="cta-bottom-teklif-al">
              Teklif Al <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </>
  );
}
